import type { MutableRefObject } from 'react'
import type { Translation } from '../../../translations'
import type { SearchResult } from '../types'

const withPdfPage = (url: string, pageNumber?: string | null) => {
    if (!url || !pageNumber) return url
    const n = parseInt(String(pageNumber), 10)
    if (!Number.isFinite(n) || n <= 0) return url
    return url.includes('#') ? `${url}&page=${n}` : `${url}#page=${n}`
}

interface SearchResultsListProps {
    results: SearchResult[]
    loading: boolean
    totalSize: number | null
    correctedQuery: string | null
    nextPageToken: string | null
    expandedResultIds: Record<string, boolean>
    onToggleResult: (id: string) => void
    loadMoreRef: MutableRefObject<HTMLDivElement | null>
    t: Translation
}

export function SearchResultsList({
    results,
    loading,
    totalSize,
    correctedQuery,
    nextPageToken,
    expandedResultIds,
    onToggleResult,
    loadMoreRef,
    t
}: SearchResultsListProps) {
    return (
        <div className="mt-4 flex-1 overflow-y-auto space-y-4 pb-24 sm:pb-6 custom-scrollbar">
            {(results.length > 0 || loading) && (
                <div className="flex items-center justify-between text-xs text-slate-500">
                    <div>
                        Showing {results.length}
                        {typeof totalSize === 'number' ? ` of ${totalSize}` : ''}
                        {nextPageToken ? ' (more available)' : ''}
                    </div>
                    {correctedQuery ? <div className="truncate">Did you mean: {correctedQuery}</div> : null}
                </div>
            )}

            {results.length === 0 && !loading && (
                <div className="mt-16 text-center text-slate-500">
                    <div className="text-2xl font-semibold sm:text-3xl">{t.searchHintTitle}</div>
                    <div className="mt-2 text-sm text-slate-400">{t.searchHintSubtitle}</div>
                </div>
            )}

            {results.map((result) => (
                <article key={result.id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
                    <div className="flex items-start justify-between gap-3">
                        <h2 className="text-lg font-bold text-blue-700 sm:text-xl">
                            <a href={withPdfPage(result.link, result.pageNumber)} target="_blank" rel="noopener noreferrer" className="hover:underline">
                                {result.title}
                            </a>
                        </h2>
                        {(result.fullText || result.snippet) && (
                            <div className="shrink-0">
                                <button
                                    type="button"
                                    onClick={() => onToggleResult(result.id)}
                                    className="rounded-full border border-slate-200 bg-white p-2 text-slate-700 hover:bg-slate-50"
                                    aria-label="More"
                                    title="More"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden="true">
                                        <path d="M12 6a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm0 8a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm0 8a2 2 0 1 1 0-4 2 2 0 0 1 0 4z" />
                                    </svg>
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="mt-2 text-sm leading-relaxed text-slate-600" dangerouslySetInnerHTML={{ __html: result.snippet }} />
                    {expandedResultIds[result.id] && (
                        <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                            <div className="mb-2 flex items-center justify-between gap-2 text-xs text-slate-600">
                                <div>
                                    {result.fullPageNumber
                                        ? `Page ${result.fullPageNumber}`
                                        : result.pageNumber
                                            ? `Page ${result.pageNumber}`
                                            : ''}
                                </div>
                                <a
                                    href={withPdfPage(result.link, result.fullPageNumber || result.pageNumber)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-700 hover:underline"
                                >
                                    Open PDF
                                </a>
                            </div>
                            <div className="text-sm leading-relaxed text-slate-700" dangerouslySetInnerHTML={{ __html: result.fullText || result.snippet }} />
                        </div>
                    )}
                    <div className="mt-3 truncate text-xs text-slate-400">Source: {result.id}</div>
                </article>
            ))}

            {loading && <div className="p-4 text-center text-slate-500">{t.loadMore}...</div>}

            <div ref={loadMoreRef} className="h-1 w-full" aria-hidden="true" />
        </div>
    )
}
